import constructQueryString from '../constructQueryString';

/**
 * Deterministically creates a key for meta information about a request based on
 * the model name and given URL params. This is used in the meta pattern to
 * automatically store request status info under different objects.
 *
 * **Note**: Ignores the `count` param as it only affects the number of items
 * returned and makes selectors more complicated. As a result, Pantheon
 * will use the same meta info for requests that use different counts but are
 * otherwise identical. Can revisit later if necessary
 *
 * @param {string} model The name of the model
 * @param {Object} params The URL params
 * @returns {string} The newly created meta key
 *
 * @public
 * @author Connor Taylor
 *
 * @example
 * const k1 = makeMetaKey(`foo`, { bar: `baz` }); // `foo?bar=baz`
 * const k2 = makeMetaKey(`foo`); // `foo`
 * const k3 = makeMetaKey(`foo`, { count: 25 }); // `foo`
 */
const makeMetaKey = (model, params = null) => {
  if (!params) {
    return model;
  }

  // Clone the object to delete the count key without affecting the original obj
  const paramsClone = { ...params };
  delete paramsClone.count;

  return `${model}${constructQueryString(paramsClone)}`;
};

/** @component */
export default makeMetaKey;
